import React, { useState } from 'react';
import Modal from 'contexts/ModalContext/Modal';
import { useModal } from 'contexts/ModalContext/ModalContext';
import apiClient from 'libs/api/apiClient';
import styled from 'styled-components';
import { LectureReview } from '../LectureReviewCard';
import InformationCircle from './information-circle.svg';
import StarActive from './star-active.svg';
import StarInactive from './star-inactive.svg';

interface InterviewMasterClassBasicReviewsModalProps {
  review: Pick<LectureReview, 'rating' | 'content'> | undefined | null;
  updateLecture: () => void;
}

function InterviewMasterClassBasicReviewsModal({
  review,
  updateLecture,
}: InterviewMasterClassBasicReviewsModalProps) {
  const [rating, setRating] = useState(review ? review.rating : 5);
  const [content, setContent] = useState(review ? review.content : '');
  const [isDisabled, setIsDisabled] = useState(!!review);
  const [isMutating, setIsMutating] = useState(false);
  const modal = useModal();

  const handleClickStar = (rating: number) => () => setRating(rating);

  const handleClickCancel = modal.close;
  const handleClickSubmit = () => {
    setIsMutating(true);

    apiClient.post('/lectures/1/reviews', { rating, content }).then(() => {
      setIsMutating(false);
      updateLecture();
      modal.close();
    });
  };

  return (
    <Modal title='리뷰 남기기' maxWidth={420}>
      <StyledContainer>
        <div className='rating'>
          <strong>{rating}점</strong>
          <span>{['별로에요', '그저그래요', '괜찮아요', '좋아요', '최고에요'][rating - 1]}</span>
        </div>

        <div className='stars'>
          {Array(5)
            .fill(0)
            .map((_, index) => (
              <button key={index} className='star' onClick={handleClickStar(index + 1)} disabled={isDisabled}>
                <img src={index < rating ? StarActive : StarInactive} alt='star' className='star-icon' />
              </button>
            ))}
        </div>

        <div className='question'>강의의 어떤 점이 좋았나요?</div>

        <textarea
          autoFocus
          className='content'
          placeholder='최소 10자 이상 입력해 주세요.'
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={isDisabled}
        />

        <div className='help-text'>
          <img alt='information-circle' src={InformationCircle} />
          <span>모든 리뷰는 익명으로 처리됩니다.</span>
        </div>

        <div className='buttons'>
          <button className='button' onClick={handleClickCancel} disabled={isMutating}>
            취소
          </button>
          <button
            className='button'
            data-type='primary'
            onClick={handleClickSubmit}
            disabled={isDisabled || isMutating}
          >
            등록
          </button>
        </div>
      </StyledContainer>
    </Modal>
  );
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  > .rating {
    display: flex;
    column-gap: 8px;
    align-items: center;
    margin-bottom: 6px;

    > strong {
      font-size: 20px;
      font-weight: 700;
      color: black;
    }

    > span {
      font-size: 14px;
      font-weight: 500;
      color: #555;
    }
  }

  > .stars {
    display: grid;
    grid-template-columns: repeat(5, 50px);
    height: 50px;

    > .star {
      all: unset;
      box-sizing: border-box;
      cursor: pointer;

      > .star-icon {
        width: 50px;
        height: 50px;
      }
    }
  }

  > .question {
    margin-top: 20px;
    margin-bottom: 12px;
    text-align: center;
    font-size: 15px;
    font-weight: 700;
    color: #555;
  }

  > .content {
    all: unset;
    box-sizing: border-box;
    width: 100%;
    background-color: #f7f7f7;
    border-radius: 10px;
    padding: 14px;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 12px;
  }

  > .help-text {
    font-size: 14px;
    color: #999;
    display: flex;
    align-items: center;
    column-gap: 4px;
    margin-bottom: 28px;
  }

  .buttons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 10px;
    width: 100%;

    > .button {
      all: unset;
      cursor: pointer;
      box-sizing: border-box;
      border-radius: 10px;
      height: 48px;
      width: 100%;
      display: grid;
      place-items: center;
      font-size: 15px;
      font-weight: 700;
      color: #555;
      border: 1px solid #e8e8e8;

      &[data-type='primary'] {
        border-color: #003159;
        background-color: #003159;
        color: white;
      }

      &:disabled {
        background-color: gray;
        cursor: not-allowed;
      }
    }
  }
`;

export default InterviewMasterClassBasicReviewsModal;
